import { pointer } from 'd3-selection';
import { axisBottom, axisLeft } from 'd3-axis';
import { format } from 'd3-format';
import './styles/graphic.scss';

const CASES_COLOR = '#F8C153'; // CNN yellow
const DEATHS_COLOR = '#F18851'; // CNN orange
const AXIS_COLOR = '#A6A6A6';
const LABEL_COLOR = '#262626';
const GRID_COLOR = '#B1B1B1';
const FONT = '"CNN Sans Display", sans-serif';

const fmt = format(',d');

const YEAR_TICKS = [1975, 1980, 1985, 1990, 1995, 2000, 2005, 2010, 2015, 2020, 2025];
const LEGEND_VALUES = [100, 1000, 10000];

const ANNOTATION_COLOR = '#888888';
const ANNOTATIONS = [
  { year: 1976, country: 'Sudan', label: 'The first recorded ebola outbreaks occurred in 1976 in what is now South Sudan and the DRC' },
  { year: 1976, country: 'Democratic Republic of the Congo', label: '' },
  { year: 2014, country: null, label: 'The 2014 oubreak in West Africa was the largest and deadliest on record' },
];

export default function graphic(props, container, isMobile) {
  const {
    initialWidth,
    initialHeight,
  } = props;

  // ── Tooltip ───────────────────────────────────────────────────────
  const tooltip = container
    .append('div')
    .attr('class', 'bubble-tooltip')
    .style('display', 'none');

  // ── SVG ───────────────────────────────────────────────────────────
  const svg = container
    .append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight)
    .attr('overflow', 'visible');

  // ── Legend at top (shared) ────────────────────────────────────────
  drawTopLegend(svg, props);

  // ── Hover hint: upper-left, below dek ────────────────────────────
  const CLICK_ICON = 'data:image/webp;base64,UklGRhICAABXRUJQVlA4TAYCAAAvG0AKEIUc29qO7dm/klS2zSqVUTmlVdrsMgU7qTwMG5WTMViVk7t4n/d5Pw0gQgpt28a2nc/+bdtmc1JycrJtm8m2/Scj2rZtc06AyNQdlUXtTUCzWkiQkDC+REKee07nKYC1WfbhYI6CnmJElrARB4QQ2nmpjBAKeYUBoqI12C7SnqCvqGy92SX2GCJqK1m2BFFzHb3FFXWqGGOHZZbZZaAQWjHGBj35poSQYZ7Nlllur84JjwFcEsIO3gpXYIRQmqJtCQXUVFllNeUTwqmxQngGs4VQTE2VVVFdVqI8o6atzEyhbB289QMn5a0ZG22G/VpaabScmiwTwnoAHskjZFTbJYRDWC0ch4tuY2lBspIysw7iqfDPcMy2AJYJoYnn+K2LcKxsP54JoYd2IUznhVCJAfaZCi2sTOxUwgghrOCTMInrwjs9OefMWCG8wl4h7FO2nE/CdB4L4RQYK4R/znslhIN4KUNDFgghzFRIvnDMXyEwUkJYzEUhPMBgJclzrknJ6+1A34WwzUp5xYLQi51CTRPCKZ6og8bMV5MVEsIiLqjFUl4nZHvRxyc1mMDFhNCTzao9hKUp4Qh3VFoJTUrUYZKfKlyGXaLcUKb5JusqvFIwQ5hKd4f9UPLWEvigosjW1ge6m2etNVZZa7nxwAG5REWJejjkiU8++uCTd65ZoY4oAg==';
  const hoverG = svg.append('g')
    .attr('transform', `translate(${props.margins.left}, 10)`);
  hoverG.append('image')
    .attr('href', CLICK_ICON)
    .attr('x', -50)
    .attr('y', -10)
    .attr('width', 12)
    .attr('height', 12);
  hoverG.append('text')
    .attr('x', -34)
    .attr('y', -10)
    .attr('text-anchor', 'start')
    .attr('dominant-baseline', 'hanging')
    .attr('fill', AXIS_COLOR)
    .style('font-size', '12px')
    .style('font-family', FONT)
    .text('Select for outbreak details');

  if (!isMobile) {
    drawDesktop(svg, tooltip, props, container);
  } else {
    drawMobile(svg, tooltip, props, container);
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Top legend: size reference only (labels float above each circle)
// ────────────────────────────────────────────────────────────────────────────
function drawTopLegend(svg, props) {
  const {
    rScale, margins, initialWidth, isMobile,
  } = props;

  const chartWidth = initialWidth - margins.left - margins.right;
  const LEGEND_W_APPROX = 200;
  const originX = margins.left + chartWidth / 2 - LEGEND_W_APPROX / 2;
  const originY = 0;

  const sizeG = svg.append('g')
    .attr('class', 'legend')
    .attr('transform', `translate(${originX}, ${originY})`);

  // Circles are bottom-aligned to a shared baseline just above the chart area.
  const baselineSVG = isMobile ? margins.top - 50 : margins.top - 34;
  const baselineLocal = baselineSVG - originY;

  const LABEL_OFFSET = 82; // x offset where first reference circle begins

  let cumX = LABEL_OFFSET;

  LEGEND_VALUES.forEach((val) => {
    const r = Math.max(2, rScale(val));
    const cx = cumX + r;
    const cy = baselineLocal - r; // bottom of circle sits on baseline

    // Label drawn first (below circle in z-order) centered above circle top
    sizeG.append('text')
      .attr('x', cx)
      .attr('y', cy - r - 4)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'auto')
      .attr('fill', AXIS_COLOR)
      .style('font-size', '11px')
      .style('font-family', FONT)
      .text(fmt(val));

    sizeG.append('circle')
      .attr('cx', cx)
      .attr('cy', cy)
      .attr('r', r)
      .attr('fill', 'none')
      .attr('stroke', AXIS_COLOR)
      .attr('stroke-width', 1);

    cumX += r * 2 + 12;
  });

  // Icon + hover text below size legend (commented out; now rendered upper-left in graphic())
  // const centerX = (LABEL_OFFSET + cumX) / 2;
  // const ICON_W = 12;
  // const iconGap = 4;
  // const COMBINED_HALF_W = (ICON_W + iconGap + 138) / 2;
  // sizeG.append('image')
  //   .attr('href', CLICK_ICON)
  //   .attr('x', centerX - COMBINED_HALF_W)
  //   .attr('y', baselineLocal + 12)
  //   .attr('width', ICON_W)
  //   .attr('height', ICON_W);
  // sizeG.append('text')
  //   .attr('x', centerX - COMBINED_HALF_W + ICON_W + iconGap)
  //   .attr('y', baselineLocal + 12)
  //   .attr('text-anchor', 'start')
  //   .attr('dominant-baseline', 'hanging')
  //   .attr('fill', AXIS_COLOR)
  //   .style('font-size', '12px')
  //   .style('font-family', FONT)
  //   .text('Hover for outbreak details');

  // Baseline tick line under the circles
  // sizeG.append('line')
  //   .attr('x1', LABEL_OFFSET)
  //   .attr('x2', cumX)
  //   .attr('y1', baselineLocal)
  //   .attr('y2', baselineLocal)
  //   .attr('stroke', GRID_COLOR)
  //   .attr('stroke-width', 0.5);
}

// ────────────────────────────────────────────────────────────────────────────
// Desktop: horizontal timeline, cases lane above deaths lane, axis at bottom
// ────────────────────────────────────────────────────────────────────────────
function drawDesktop(svg, tooltip, props, container) {
  const {
    casesData,
    deathsData,
    width,
    initialWidth,
    initialHeight,
    margins,
    yearScale,
    layout,
  } = props;

  const {
    casesLaneCenterY, deathsLaneCenterY, axisY, separatorY,
  } = layout;

  // ── Year axis (bottom) ────────────────────────────────────────────
  const xAxis = axisBottom(yearScale)
    .tickValues(YEAR_TICKS)
    .tickFormat(d => `${d}`)
    .tickSize(4)
    .tickPadding(5);

  const axisG = svg.append('g')
    .attr('class', 'axis x')
    .attr('transform', `translate(0, ${axisY})`)
    .call(xAxis);

  axisG.select('.domain').remove();
  axisG.selectAll('.tick line')
    .attr('stroke', GRID_COLOR)
    .attr('stroke-width', 0.5);
  axisG.selectAll('.tick text')
    .attr('fill', AXIS_COLOR)
    .style('font-size', '11px')
    .style('font-family', FONT);

  // Vertical grid lines spanning from top of data to axis
  const gridTop = margins.top;
  const gridBottom = axisY;

  svg.append('g')
    .attr('class', 'grid-lines')
    .selectAll('line')
    .data(YEAR_TICKS)
    .join('line')
    .attr('x1', d => yearScale(d))
    .attr('x2', d => yearScale(d))
    .attr('y1', gridTop)
    .attr('y2', gridBottom)
    .attr('stroke', GRID_COLOR)
    .attr('stroke-width', 0.5)
    .attr('stroke-dasharray', '2,3');

  // Thin separator line between the two lanes
  svg.append('line')
    .attr('class', 'lane-divider')
    .attr('x1', margins.left)
    .attr('x2', margins.left + width)
    .attr('y1', separatorY)
    .attr('y2', separatorY)
    .attr('stroke', GRID_COLOR)
    .attr('stroke-width', 0.5);

  // ── Row labels ────────────────────────────────────────────────────
  const labelX = margins.left - 8;

  svg.append('text')
    .attr('class', 'lane-label')
    .attr('x', labelX)
    .attr('y', casesLaneCenterY)
    .attr('text-anchor', 'end')
    .attr('dominant-baseline', 'middle')
    .text('Cases');

  svg.append('text')
    .attr('class', 'lane-label')
    .attr('x', labelX)
    .attr('y', deathsLaneCenterY)
    .attr('text-anchor', 'end')
    .attr('dominant-baseline', 'middle')
    .text('Deaths');

  // ── Bubbles ───────────────────────────────────────────────────────
  svg.selectAll('circle.cases')
    .data(casesData)
    .join('circle')
    .attr('class', 'bubble cases')
    .attr('cx', d => d.x)
    .attr('cy', d => d.y)
    .attr('r', d => d.r)
    .attr('fill', CASES_COLOR)
    .attr('stroke', '#fff')
    .attr('stroke-width', 1.5)
    .on('mouseover touchstart', (event, d) => {
      showTooltip(event, d, 'cases', tooltip, container);
    })
    .on('mousemove', (event) => {
      moveTooltip(event, tooltip, container);
    })
    .on('mouseout touchend', () => tooltip.style('display', 'none'));

  svg.selectAll('circle.deaths')
    .data(deathsData)
    .join('circle')
    .attr('class', 'bubble deaths')
    .attr('cx', d => d.x)
    .attr('cy', d => d.y)
    .attr('r', d => d.r)
    .attr('fill', DEATHS_COLOR)
    .attr('stroke', '#fff')
    .attr('stroke-width', 1.5)
    .on('mouseover touchstart', (event, d) => {
      showTooltip(event, d, 'deaths', tooltip, container);
    })
    .on('mousemove', (event) => {
      moveTooltip(event, tooltip, container);
    })
    .on('mouseout touchend', () => tooltip.style('display', 'none'));

  drawAnnotations(svg, props);
}

// ────────────────────────────────────────────────────────────────────────────
// Mobile: vertical timeline, two columns
// ────────────────────────────────────────────────────────────────────────────
function drawMobile(svg, tooltip, props, container) {
  const {
    casesData,
    deathsData,
    initialWidth,
    initialHeight,
    margins,
    yearScale,
    layout,
  } = props;

  const {
    casesColCenterX, deathsColCenterX, colWidth, yearAxisHeight,
  } = layout;

  // ── Column header labels ───────────────────────────────────────────
  // These sit just below the legend, above the data area
  const headerY = margins.top - 6;

  svg.append('text')
    .attr('class', 'lane-label')
    .attr('x', casesColCenterX)
    .attr('y', headerY)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'auto')
    .text('Cases');

  svg.append('text')
    .attr('class', 'lane-label')
    .attr('x', deathsColCenterX)
    .attr('y', headerY)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'auto')
    .text('Deaths');

  // ── Center divider between the two columns ─────────────────────────
  const divX = margins.left + colWidth;
  svg.append('line')
    .attr('class', 'lane-divider')
    .attr('x1', divX)
    .attr('x2', divX)
    .attr('y1', margins.top)
    .attr('y2', margins.top + yearAxisHeight)
    .attr('stroke', GRID_COLOR)
    .attr('stroke-width', 0.5);

  // ── Year axis (left side) ──────────────────────────────────────────
  const yAxis = axisLeft(yearScale)
    .tickValues(YEAR_TICKS)
    .tickFormat(d => `${d}`)
    .tickSize(4)
    .tickPadding(5);

  const axisG = svg.append('g')
    .attr('class', 'axis y')
    .attr('transform', `translate(${margins.left}, 0)`)
    .call(yAxis);

  axisG.select('.domain').remove();
  axisG.selectAll('.tick line')
    .attr('stroke', GRID_COLOR)
    .attr('stroke-width', 0.5);
  axisG.selectAll('.tick text')
    .attr('fill', AXIS_COLOR)
    .style('font-size', '11px')
    .style('font-family', FONT);

  // Horizontal grid lines
  svg.append('g')
    .attr('class', 'grid-lines')
    .selectAll('line')
    .data(YEAR_TICKS)
    .join('line')
    .attr('x1', margins.left)
    .attr('x2', initialWidth - margins.right)
    .attr('y1', d => yearScale(d))
    .attr('y2', d => yearScale(d))
    .attr('stroke', GRID_COLOR)
    .attr('stroke-width', 0.5)
    .attr('stroke-dasharray', '2,3');

  // ── Bubbles ───────────────────────────────────────────────────────
  svg.selectAll('circle.cases')
    .data(casesData)
    .join('circle')
    .attr('class', 'bubble cases')
    .attr('cx', d => d.x)
    .attr('cy', d => d.y)
    .attr('r', d => d.r)
    .attr('fill', CASES_COLOR)
    .attr('stroke', '#fff')
    .attr('stroke-width', 1.5)
    .on('mouseover touchstart', (event, d) => {
      showTooltip(event, d, 'cases', tooltip, container);
    })
    .on('mousemove', (event) => {
      moveTooltip(event, tooltip, container);
    })
    .on('mouseout touchend', () => tooltip.style('display', 'none'));

  svg.selectAll('circle.deaths')
    .data(deathsData)
    .join('circle')
    .attr('class', 'bubble deaths')
    .attr('cx', d => d.x)
    .attr('cy', d => d.y)
    .attr('r', d => d.r)
    .attr('fill', DEATHS_COLOR)
    .attr('stroke', '#fff')
    .attr('stroke-width', 1.5)
    .on('mouseover touchstart', (event, d) => {
      showTooltip(event, d, 'deaths', tooltip, container);
    })
    .on('mousemove', (event) => {
      moveTooltip(event, tooltip, container);
    })
    .on('mouseout touchend', () => tooltip.style('display', 'none'));

  drawAnnotations(svg, props);
}

// ────────────────────────────────────────────────────────────────────────────
// Annotations
// ────────────────────────────────────────────────────────────────────────────
function wordWrap(text, maxLineLength) {
  if (!text) return [];
  const words = text.split(' ');
  const lines = [];
  let line = '';
  words.forEach((word) => {
    if (!line) {
      line = word;
    } else if ((`${line} ${word}`).length <= maxLineLength) {
      line += ` ${word}`;
    } else {
      lines.push(line);
      line = word;
    }
  });
  if (line) lines.push(line);
  return lines;
}

function findNode(nodes, ann) {
  if (ann.country) {
    return nodes.find(n => n.year === ann.year && n.country === ann.country) || null;
  }
  return nodes.filter(n => n.year === ann.year).sort((a, b) => b.r - a.r)[0] || null;
}

function drawDesktopCallout(svg, node, label) {
  if (!label) return;
  const GAP = 3;
  const LINE_H = 14;
  const lines = wordWrap(label, 25);
  const lastLineY = node.y - 75;

  svg.append('line')
    .attr('x1', node.x).attr('y1', lastLineY + 6)
    .attr('x2', node.x)
    .attr('y2', node.y - node.r - GAP)
    .attr('stroke', ANNOTATION_COLOR)
    .attr('stroke-width', 1)
    .attr('pointer-events', 'none');

  const textEl = svg.append('text')
    .attr('text-anchor', 'start')
    .attr('dominant-baseline', 'auto')
    .attr('fill', ANNOTATION_COLOR)
    .style('font-size', '12px')
    .style('font-family', FONT);

  lines.forEach((line, i) => {
    textEl.append('tspan')
      .attr('x', node.x)
      .attr('y', lastLineY - (lines.length - 1 - i) * LINE_H)
      .text(line);
  });
}
// use the deaths node as the line source if it exists,
// since it's in the rightmost data column, closest to the annotation area
// const lineSourceNode = dNode || cNode;

function drawMobileCallout(svg, node, label, layout) {
  if (!label) return;
  const { annotationColX } = layout;
  const LINE_H = 12;
  const lines = wordWrap(label, 12);
  const totalH = lines.length * LINE_H;
  const firstLineY = node.y;

  svg.append('line')
    .attr('x1', node.x + 20).attr('y1', firstLineY)
    .attr('x2', annotationColX - 3)
    .attr('y2', firstLineY)
    .attr('stroke', ANNOTATION_COLOR)
    .attr('stroke-width', 1)
    .attr('pointer-events', 'none');

  const textEl = svg.append('text')
    .attr('dominant-baseline', 'auto')
    .attr('fill', ANNOTATION_COLOR)
    .style('font-size', '12px')
    .style('font-family', FONT);

  lines.forEach((line, i) => {
    textEl.append('tspan')
      .attr('x', annotationColX)
      .attr('y', firstLineY + i * LINE_H)
      .text(line);
  });
}

function drawAnnotations(svg, props) {
  const {
    casesData, deathsData, isMobile, layout,
  } = props;

  ANNOTATIONS.forEach((ann) => {
    const cNode = findNode(casesData, ann);
    const dNode = findNode(deathsData, ann);

    // highlight rings on both cases and deaths bubbles
    [cNode, dNode].filter(Boolean).forEach((n) => {
      svg.append('circle')
        .attr('cx', n.x)
        .attr('cy', n.y)
        .attr('r', n.r)
        .attr('fill', 'none')
        .attr('stroke', ANNOTATION_COLOR)
        .attr('stroke-width', 1.5)
        .attr('pointer-events', 'none');
    });

    const anchorNode = cNode || dNode;
    const mobileAnchorNode = dNode || cNode;
    if (!anchorNode) return;

    if (!isMobile) {
      drawDesktopCallout(svg, anchorNode, ann.label);
    } else {
      drawMobileCallout(svg, mobileAnchorNode, ann.label, layout);
    }
  });
}

// ────────────────────────────────────────────────────────────────────────────
// Tooltip
// ────────────────────────────────────────────────────────────────────────────
function buildTooltipHTML(d, lane) {
  const value = lane === 'cases' ? d.cases : d.deaths;
  const label = lane === 'cases' ? 'Cases' : 'Deaths';
  return `<strong>${d.country}</strong><br>${d.year}<br>${label}: <strong>${fmt(value)}</strong>`;
}

function showTooltip(event, d, lane, tooltip, container) {
  tooltip
    .style('display', 'block')
    .html(buildTooltipHTML(d, lane));
  moveTooltip(event, tooltip, container);
}

function moveTooltip(event, tooltip, container) {
  const containerNode = container.node();
  const rect = containerNode.getBoundingClientRect();
  const [ex, ey] = pointer(event, containerNode);

  const tw = tooltip.node().offsetWidth || 150;
  const th = tooltip.node().offsetHeight || 60;
  const pad = 10;

  let left = ex + pad;
  let top = ey - th / 2;

  // Flip to the left if it would overflow the right edge
  if (left + tw > rect.width - 4) left = ex - tw - pad;
  // Clamp to left edge
  left = Math.max(4, left);

  // Clamp top/bottom
  if (top < 0) top = 0;
  if (top + th > rect.height) top = rect.height - th;

  tooltip
    .style('left', `${left}px`)
    .style('top', `${top}px`);
}
